import { DOCS_URLS } from "@p0security/frontend-core";
import { VALID_AWS_REGIONS } from "@p0security/integrations-aws-frontend";
import {
  GOVCLOUD_REGIONS,
  P0_DEFAULT_AWS_REGION,
} from "@p0security/integrations-aws-shared/constants";
import { parseCatalogKey } from "@p0security/integrations-databricks-shared/catalog-key";
import { DATABRICKS_INTEGRATION_KEY } from "@p0security/integrations-databricks-shared/constants";
import {
  accountAdminRole,
  renderStillManaged,
  workspaceAdminRole,
} from "@p0security/integrations-databricks-shared/switches";
import type { DatabricksComponents } from "@p0security/integrations-databricks-shared/types";
import {
  type FrontendInstaller,
  integrationOptionProvider,
} from "@p0security/integrations-installer";
import {
  installedIds,
  installedItem,
} from "@p0security/shared/install/installed";
import type { Instructions } from "@p0security/shared/install/types";
import type { NewConfig } from "@p0security/shared/integrations/shared";
import { Typography } from "@p0security/ui";
import type { ReactNode } from "react";

import { CatalogSuggestions } from "./CatalogSuggestions.tsx";
import {
  type HeldAdminRequestsCheck,
  HeldAdminRequestsChecker,
} from "./HeldAdminRequestsChecker.tsx";
import {
  PROVIDERS_TERRAFORM,
  accountAdminTerraform,
  accountTerraform,
  applicationIdOutput,
  catalogTerraform,
  connectorTerraform,
  workspaceTerraform,
} from "./iac.ts";
import { installedKeys, itemLabel } from "./options.ts";

const { Link, Paragraph, Text } = Typography;

type DatabricksInstaller =
  FrontendInstaller<DatabricksComponents>["components"];

// The connector's ARN is built in the `aws` partition, so GovCloud regions
// can't host it. P0's default region comes first, as the preselected one.
const CONNECTOR_REGIONS = [
  P0_DEFAULT_AWS_REGION,
  ...VALID_AWS_REGIONS.filter(
    (region) =>
      region !== P0_DEFAULT_AWS_REGION && !GOVCLOUD_REGIONS.includes(region)
  ),
];

/**
 * Whether an admin switch is on. A switch the customer never touched may be
 * unset, which is off.
 */
const isOn = (value: unknown): value is true => value === true;

/**
 * The step that applies an admin switch's Terraform, which `change` says.
 * Turning a switch on, the customer applies it first, since P0 checks the
 * role when they click "Next". Turning it off, they click "Next" first: P0
 * refuses while open requests still need the role, and the role must still
 * be there to revoke them.
 */
const SwitchTerraformHeader = ({
  change,
  isTurningOn,
}: {
  change: string;
  isTurningOn: boolean;
}) => (
  <Paragraph>
    {isTurningOn
      ? `In the Terraform configuration that holds the account, ${change}, apply it as a Databricks account admin with AWS access to the connector's account, then click "Next".`
      : `Click "Next" first. Once P0 has saved the change, open the Terraform configuration that holds the account, ${change}, and apply it as a Databricks account admin with AWS access to the connector's account.`}
  </Paragraph>
);

/**
 * The instructions for turning an admin switch off. They ask P0 first which
 * requests on `scope` may still hold the role (`HeldAdminRequestsChecker`),
 * keep the answer in the switch's instruction state, and show the Terraform
 * that takes the role away, `command`, only once none does, or if P0
 * couldn't check. The configurer refuses the change on submit either way.
 *
 * @param requests What the requests are, e.g. `account-admin requests`.
 * @param role What the refusal calls the role (see `switches.ts`).
 * @param withoutRole What the principal doesn't need once the switch is off.
 */
const turnOffInstructions = ({
  change,
  command,
  requests,
  role,
  scope,
  setState,
  state,
  withoutRole,
}: {
  change: string;
  command: string;
  requests: string;
  role: string;
  scope: string;
  /** The switch's instruction state, which only this writes. */
  setState: (state: { check?: HeldAdminRequestsCheck }) => unknown;
  state: Readonly<{ check?: HeldAdminRequestsCheck }>;
  withoutRole: ReactNode;
}): Instructions<ReactNode> => {
  const { check } = state;
  const held = check?.type === "checked" ? check.requestIds : [];
  return {
    help: (
      <>
        <HeldAdminRequestsChecker scope={scope} setState={setState} />
        {withoutRole}
        {!check ? (
          <Paragraph>
            Checking whether open {requests} still need the role...
          </Paragraph>
        ) : check.type === "failed" ? (
          <Paragraph>
            <Text type="warning">
              P0 couldn&apos;t check whether open {requests} still need the
              role: {check.reason}
            </Text>{" "}
            P0 needs the role to revoke them, so it refuses to turn this off
            while any are open.
          </Paragraph>
        ) : held.length ? (
          <Paragraph>
            <Text type="warning">
              {renderStillManaged({ role, requestIds: held })}
            </Text>{" "}
            P0 checks again every few seconds, and shows the Terraform to apply
            once they have ended.
          </Paragraph>
        ) : null}
      </>
    ),
    ...(check && !held.length
      ? {
          commands: {
            iac: [
              {
                header: (
                  <SwitchTerraformHeader change={change} isTurningOn={false} />
                ),
                command,
              },
            ],
          },
        }
      : {}),
  };
};

/** The command that prints the account's service principal application ID. */
const ApplicationIdCommand = ({ accountId }: { accountId: string }) => (
  <Text code>terraform output -raw {applicationIdOutput(accountId)}</Text>
);

const connectorInstaller: DatabricksInstaller["connector"] = {
  optionProvider: integrationOptionProvider("connector", [
    { component: "iam-write", integration: "aws" },
  ]),
  // The connector's AWS account is chosen from those, so without one the
  // choice would be empty, with nothing to say why.
  prerequisiteMessages: async ({ installed: { aws } }) => {
    // An installed integration's config isn't typed by its components.
    if (!aws || !installedIds<string, NewConfig>("iam-write", aws).length)
      return (
        <div>
          Install P0&apos;s AWS IAM management for the connector&apos;s AWS
          account first: P0 invokes the connector as its role.
        </div>
      );
  },
  instructions: (context, awsAccountId, item) => ({
    help: (
      <Paragraph>
        P0 reaches Databricks through a connector Lambda in AWS account{" "}
        {awsAccountId}. The connector signs in to Databricks with its AWS
        identity, so no secret is stored anywhere.
      </Paragraph>
    ),
    commands: {
      iac: [
        {
          header: (
            <Paragraph>
              Your Terraform configuration needs these providers. If it already
              has a <Text code>required_providers</Text> block, add them to it.
              If a module in it pins the AWS provider to 5.x, as P0&apos;s RDS
              security perimeter and Postgres or MySQL connector modules do,
              apply this from a configuration of its own:
            </Paragraph>
          ),
          command: PROVIDERS_TERRAFORM,
        },
        {
          header: (
            <Paragraph>
              Then add the following, apply it as an AWS admin of account{" "}
              {awsAccountId}, and click &quot;Next&quot;.
            </Paragraph>
          ),
          command: connectorTerraform(context, awsAccountId, item),
        },
      ],
    },
  }),
  items: {
    region: {
      optionProvider: async () => CONNECTOR_REGIONS.map((id) => ({ id })),
    },
  },
};

const accountInstaller: DatabricksInstaller["account"] = {
  prerequisiteMessages: async (context) => {
    if (!installedKeys(context, "connector").size)
      return (
        <div>
          Install the Databricks connector first: P0 reaches each account
          through it.
        </div>
      );
  },
  instructions: (_context, accountId, item) => ({
    help: (
      <Paragraph>
        P0 manages Databricks account {accountId} through a service principal
        there, which the connector signs in as with its AWS identity. It holds
        no admin role unless you turn on account-admin requests, which give it
        account admin.
      </Paragraph>
    ),
    commands: {
      iac: [
        {
          header: (
            <Paragraph>
              Add the following to the Terraform configuration that holds the
              connector, and apply it as a Databricks account admin with AWS
              access to the connector&apos;s account. Then click
              &quot;Next&quot;, and enter the value that{" "}
              <ApplicationIdCommand accountId={accountId} /> prints.
            </Paragraph>
          ),
          command: accountTerraform(accountId, item),
        },
      ],
    },
  }),
  items: {
    connector: {
      optionProvider: integrationOptionProvider("account", [
        { component: "connector", integration: DATABRICKS_INTEGRATION_KEY },
      ]),
    },
    applicationId: {
      labeler: (_context, accountId) => (
        <>
          The service principal&apos;s application ID. Run{" "}
          <ApplicationIdCommand accountId={accountId} /> where you applied the
          account&apos;s Terraform.
        </>
      ),
    },
    // Shown when the switch changes, before the configure step is submitted.
    isAccountAdminEnabled: {
      instructions: (
        _context,
        accountId,
        _item,
        isAccountAdminEnabled,
        state,
        setState
      ) =>
        isOn(isAccountAdminEnabled)
          ? {
              help: (
                <Paragraph>
                  To grant account-admin requests, P0&apos;s service principal
                  needs account admin in Databricks account {accountId} itself.
                </Paragraph>
              ),
              commands: {
                iac: [
                  {
                    header: (
                      <SwitchTerraformHeader
                        change="add the following"
                        isTurningOn
                      />
                    ),
                    command: accountAdminTerraform(accountId),
                  },
                ],
              },
            }
          : turnOffInstructions({
              change: "remove the following",
              command: accountAdminTerraform(accountId),
              requests: "account-admin requests",
              role: accountAdminRole(accountId),
              scope: accountId,
              setState,
              state,
              withoutRole: (
                <Paragraph>
                  Without account-admin requests, P0&apos;s service principal
                  doesn&apos;t need account admin in Databricks account{" "}
                  {accountId}.
                </Paragraph>
              ),
            }),
    },
  },
};

const workspaceInstaller: DatabricksInstaller["workspace"] = {
  prerequisiteMessages: async (context) => {
    if (!installedKeys(context, "account").size)
      return (
        <div>
          Install a Databricks account first: P0 reaches each workspace through
          one.
        </div>
      );
  },
  instructions: (_context, workspaceId, item) => ({
    help: (
      <Paragraph>
        P0&apos;s service principal is assigned to workspace {workspaceId} as a
        user, which is how P0 reaches the catalogs you add through it. It
        becomes a workspace admin only if you turn on workspace-admin requests.
      </Paragraph>
    ),
    commands: {
      iac: [
        {
          header: (
            <Paragraph>
              Add the following to the Terraform configuration that holds the
              account, apply it as a Databricks account admin with AWS access to
              the connector&apos;s account, then click &quot;Next&quot;.
            </Paragraph>
          ),
          command: workspaceTerraform(workspaceId, item, {
            admin: isOn(item.isWorkspaceAdminEnabled),
          }),
        },
      ],
    },
  }),
  items: {
    account: {
      optionProvider: integrationOptionProvider("workspace", [
        { component: "account", integration: DATABRICKS_INTEGRATION_KEY },
      ]),
    },
    // Shown when the switch changes, before the configure step is submitted.
    isWorkspaceAdminEnabled: {
      instructions: (
        _context,
        workspaceId,
        item,
        isWorkspaceAdminEnabled,
        state,
        setState
      ) => {
        const change =
          "replace the workspace's permission assignment with the following";
        return isOn(isWorkspaceAdminEnabled)
          ? {
              help: (
                <Paragraph>
                  To grant workspace-admin requests, P0&apos;s service principal
                  needs to be an admin of workspace {workspaceId} itself.
                </Paragraph>
              ),
              commands: {
                iac: [
                  {
                    header: (
                      <SwitchTerraformHeader change={change} isTurningOn />
                    ),
                    command: workspaceTerraform(workspaceId, item, {
                      admin: true,
                    }),
                  },
                ],
              },
            }
          : turnOffInstructions({
              change,
              command: workspaceTerraform(workspaceId, item, { admin: false }),
              requests: "workspace-admin requests on it",
              role: workspaceAdminRole({
                workspaceId,
                workspaceName: item.workspaceName,
              }),
              scope: workspaceId,
              setState,
              state,
              withoutRole: (
                <Paragraph>
                  Without workspace-admin requests, P0&apos;s service principal
                  only needs to be a user of workspace {workspaceId}.
                </Paragraph>
              ),
            });
      },
    },
  },
};

const catalogInstaller: DatabricksInstaller["catalog"] = {
  // The installer's own "add a catalog" step asks only for the item key, which
  // names the workspace too: the backend saves the key's workspace as the
  // item's. This describes the key, and suggests what to type in it. The step
  // hands its labeler the form, whose `id` is the key as typed so far.
  labeler: (context, _id, _item, { id: typedKey }) => (
    <CatalogSuggestions context={context} typedKey={typedKey} />
  ),
  prerequisiteMessages: async (context) => {
    if (!installedKeys(context, "workspace").size)
      return (
        <div>
          Install a Databricks workspace first: P0 reaches each catalog through
          one.
        </div>
      );
  },
  instructions: (context, key) => {
    const parsed = parseCatalogKey(key);
    // Instructions are always returned: without them, the installer skips
    // straight to verifying.
    if (!parsed) {
      return {
        help: (
          <Paragraph>
            {key} isn&apos;t a catalog key, which names the catalog and its
            workspace. Remove it and add the catalog again.
          </Paragraph>
        ),
      };
    }
    // From the key, as the backend reads it, so that the account and the
    // workspace the Terraform names come from one place.
    const accountId = installedItem(
      "workspace",
      context.config,
      parsed.workspaceId
    )?.account;
    if (!accountId) {
      return {
        help: (
          <Paragraph>
            Workspace {parsed.workspaceId} isn&apos;t installed in P0. Install
            it before this catalog.
          </Paragraph>
        ),
      };
    }
    return {
      help: (
        <Paragraph>
          P0&apos;s service principal gets <Text code>MANAGE</Text> on catalog{" "}
          {parsed.catalog}, so that P0 can grant and revoke privileges on it and
          on every schema and table in it.
        </Paragraph>
      ),
      commands: {
        iac: [
          {
            header: (
              <Paragraph>
                Add the following to the Terraform configuration that holds the
                account, and apply it as a Databricks account admin who is also
                the catalog&apos;s owner, holds <Text code>MANAGE</Text> on it,
                or is a metastore admin. Account admin alone doesn&apos;t give{" "}
                <Text code>MANAGE</Text> on a catalog. Whoever applies it also
                needs AWS access to the connector&apos;s account, since every
                apply of that configuration refreshes the connector&apos;s
                resources. Then click &quot;Next&quot;.
              </Paragraph>
            ),
            command: catalogTerraform({ ...parsed, accountId }),
          },
        ],
      },
    };
  },
  // The configure step's only element is hidden, so without this it would be
  // a bare "Finish" button.
  footer: (context, key) => {
    const parsed = parseCatalogKey(key);
    if (!parsed) return null;
    const workspace = installedItem(
      "workspace",
      context.config,
      parsed.workspaceId
    );
    return (
      <Paragraph>
        P0 reaches catalog {parsed.catalog} through workspace{" "}
        {itemLabel(parsed.workspaceId, workspace?.label)}.
      </Paragraph>
    );
  },
  items: {},
};

export const databricksInstaller: FrontendInstaller<DatabricksComponents> = {
  components: {
    connector: connectorInstaller,
    account: accountInstaller,
    workspace: workspaceInstaller,
    catalog: catalogInstaller,
  },
  preamble: (
    <Paragraph>
      Install the connector first, then each Databricks account, workspace and
      catalog that P0 should manage. See{" "}
      <Link
        href={DOCS_URLS.DATABRICKS_BEFORE_YOU_BEGIN}
        rel="noopener noreferrer"
        target="_blank"
      >
        Before you begin
      </Link>{" "}
      for the access and tools each step needs. P0 doesn&apos;t create
      Databricks users: each requestor needs one already, and P0 grants Unity
      Catalog privileges to the user whose username is the requestor&apos;s P0
      email.
    </Paragraph>
  ),
};
